import { useState } from 'react';
import { LifeBuoy, MessageSquarePlus, Menu, Home } from 'lucide-react';
import { redirectToLogout } from '@goodsteward/gs-auth-spa';
import { homeAppUrl, UserMenu } from '@goodsteward/gs-ui';
import { useAuthStore } from '@/stores/auth';
import { logoutApi } from '@/api/auth';
import { getCognitoConfig } from '@/auth/config';
import { FeedbackDialog } from '@/features/feedback/FeedbackDialog';
import { NotificationDrawer } from './NotificationDrawer';

interface TopBarProps {
    /** Whether the mobile hamburger should be rendered. False for parent
     *  accounts, who don't see a sidebar at all. */
    showMobileNav?: boolean;
    /** Opens the mobile nav drawer. */
    onMobileNavOpen?: () => void;
}

export function TopBar({ showMobileNav = false, onMobileNavOpen }: TopBarProps) {
    const user = useAuthStore((s) => s.user);
    const practice = useAuthStore((s) => s.practice);
    const [feedbackOpen, setFeedbackOpen] = useState(false);

    // user can be null in a half-loaded session (token persisted but
    // /api/me failed). RequireAuth recovers from that state, but we
    // still render a minimal TopBar so a stuck user always has a
    // visible sign-out path — UserMenu falls back to a "?" avatar.
    const isParent = user?.roleType === 'parent';

    async function handleLogout() {
        try {
            await logoutApi();
        } catch {
            // Best-effort — local sign-out and IdP bounce must proceed
            // even if the server call fails (it's a no-op today, but a
            // network blip shouldn't trap the user).
        }
        // Do NOT call logout() before the redirect. setState triggers
        // a re-render where RequireAuth sees no token, fires
        // redirectToHostedUi, and that second window.location.assign
        // cancels the /logout navigation — Cognito's SSO cookie is
        // never dropped and the user gets auto-signed-in on the next
        // mount. The redirect tears down the SPA on its own.
        redirectToLogout(getCognitoConfig());
    }

    return (
        <header className="h-[var(--size-topbar)] bg-surface border-b border-border flex items-center justify-between gap-2 px-3 sm:px-6 shrink-0 sticky top-0 z-20">
            <div className="flex items-center gap-2 min-w-0">
                {showMobileNav && (
                    <button
                        type="button"
                        onClick={onMobileNavOpen}
                        aria-label="Open navigation"
                        className="md:hidden -ml-1 p-2 rounded-md text-text-muted hover:text-text hover:bg-surface-muted transition-colors"
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                )}
                <div className="min-w-0">
                    <div className="font-brand text-base text-text truncate">
                        {practice?.name ?? '—'}
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-2">
                {user && !isParent && (
                    <a
                        href={homeAppUrl('pma')}
                        className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm text-text-muted hover:text-text hover:bg-surface-muted transition-colors"
                    >
                        <Home className="w-4 h-4" />
                        <span className="hidden sm:inline">Home</span>
                    </a>
                )}
                {user && isParent && (
                    <a
                        href="https://support.goodstewardaba.com/pma/parents/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm text-text-muted hover:text-text hover:bg-surface-muted transition-colors"
                    >
                        <LifeBuoy className="w-4 h-4" />
                        <span className="hidden sm:inline">Support</span>
                    </a>
                )}
                {user && <NotificationDrawer />}

                <UserMenu
                    name={user ? `${user.first} ${user.last}` : undefined}
                    secondary={user ? user.roleName : 'Session unavailable'}
                    email={user?.email}
                    items={user ? [{
                        label: 'Submit feedback',
                        icon: <MessageSquarePlus className="w-4 h-4" aria-hidden />,
                        onSelect: () => setFeedbackOpen(true),
                    }] : []}
                    onSignOut={handleLogout}
                />
            </div>

            <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
        </header>
    );
}
